/* Form source verification, Yuropet v1. Stores only tab-local form identity, never customer details. */
(() => {
  'use strict';
  const KEY = 'yp-contact-form-pending-v1';
  const LIFE = 12 * 60 * 1000;
  const kindFor = (form) => {
    if (!form) return '';
    if (form.id === 'YuropetFooterContact') return 'footer';
    if (form.id === 'YuropetAboutContactForm') return 'contact';
    if (form.id === 'OEMProjectForm') return 'oem';
    if (form.id && form.id.startsWith('YuropetRequestForm-')) {
      const type = form.querySelector('input[name="contact[Request type]"]')?.value || '';
      return type === 'Request a Quote' ? 'quote' : 'request:' + type;
    }
    return '';
  };
  const allForms = [...document.querySelectorAll('form')].filter(kindFor);
  const getPending = () => {
    try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); }
    catch (_e) { return null; }
  };
  const clearPending = () => {
    try { sessionStorage.removeItem(KEY); } catch (_e) { /* storage restricted */ }
  };
  const pending = getPending();
  const onSamePage = pending && pending.path === window.location.pathname;
  const fresh = pending && typeof pending.at === 'number' &&
    Date.now() >= pending.at && Date.now() - pending.at < LIFE;
  const posted = new URLSearchParams(window.location.search).get('contact_posted') === 'true';
  const pendingOrigin = onSamePage && fresh
    ? allForms.find((form) => kindFor(form) === pending.kind && form.id === pending.id)
    : null;

  // Shopify's return URL can identify the submitted form even if a challenge,
  // native submit path, or restricted sessionStorage lost the pending record.
  // A matching hash alone is insufficient: require server-rendered feedback.
  let returnHash = window.location.hash.slice(1);
  try { returnHash = decodeURIComponent(returnHash); } catch (_e) { /* keep literal hash */ }
  const hashedForm = allForms.find((form) => form.id === returnHash);
  const serverSuccess = (form) => form?.querySelector('[data-yp-contact-feedback="success"], .yp-oem-v5__success');
  const serverError = (form) => form?.querySelector('[data-yp-contact-feedback="error"], .yp-oem-v5__errors');
  const returnedOrigin = hashedForm && (posted ? serverSuccess(hashedForm) : serverError(hashedForm))
    ? hashedForm : null;
  // The explicit Shopify return anchor takes precedence over any stale
  // pending record, preserving isolation on pages with a Footer contact form.
  const origin = returnedOrigin || pendingOrigin;
  const result = origin && posted && serverSuccess(origin) ? 'success'
    : origin && !posted && serverError(origin) ? 'error' : '';
  const kind = result ? kindFor(origin) : '';
  window.YuropetContactState = Object.freeze({
    kind, result, isSuccess: (candidate) => result === 'success' && kind === candidate
  });
  allForms.forEach((form) => {
    const matched = !!result && form === origin;
    form.dataset.ypContactVerified = matched ? result : 'none';
    form.querySelectorAll('[data-yp-contact-feedback], .yp-oem-v5__success, .yp-oem-v5__errors').forEach((node) => {
      const feedbackKind = node.dataset.ypContactFeedback ||
        (node.classList.contains('yp-oem-v5__success') ? 'success' : 'error');
      node.hidden = !(matched && feedbackKind === result);
      if (form.id === 'OEMProjectForm') node.setAttribute('role', feedbackKind === 'success' ? 'status' : 'alert');
    });
  });
  // Do not replay a successful submission merely because a return URL is
  // refreshed or bookmarked. Preserve all other query parameters and anchor.
  if (result === 'success' && posted) {
    try {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('contact_posted');
      window.history.replaceState(window.history.state, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
    } catch (_e) { /* page feedback still works if history is restricted */ }
  }
  // A Shopify challenge can interrupt navigation before a result is returned.
  // Retain fresh pending identity across that interruption; consume on result.
  if (pending && (result || !fresh)) clearPending();
  // Capture a valid final submit before a third-party Shopify interceptor can
  // preventDefault. Do not record Footer's initial email-only advance action.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const formKind = kindFor(form);
    if (!formKind || !form.checkValidity()) return;
    if (form.id === 'YuropetFooterContact' &&
      form.querySelector('[data-yp-footer-message-stage]')?.hidden) return;
    // OEM's existing Liquid file is over Shopify's 256 KB edit limit.
    // Add the supported return_to parameter at native submit time without rewriting that file.
    if (form.id === 'OEMProjectForm' && !form.querySelector('input[name="return_to"]')) {
      const back = document.createElement('input');
      back.type = 'hidden';
      back.name = 'return_to';
      back.value = 'back';
      form.appendChild(back);
    }
    try {
      sessionStorage.setItem(KEY, JSON.stringify({
        kind: formKind, id: form.id, path: window.location.pathname, at: Date.now()
      }));
    } catch (_e) { /* Shopify return anchor remains an identity fallback */ }
  }, true);
  document.dispatchEvent(new CustomEvent('yuropet:contact-state', {
    detail: { kind, result }
  }));
})();


/* yp-ga4-confirmed-inquiry-v1 — append after existing contact-state verifier.
 * No form contents, email, name, company, phone or message enter the payload.
 */
(() => {
  'use strict';
  const state = window.YuropetContactState;
  if (!state || state.result !== 'success') return;
  const names = {quote:'quote_request', footer:'footer_contact', contact:'contact_page', oem:'oem_project'};
  const name = names[state.kind];
  if (!name || !document.querySelector('form[data-yp-contact-verified="success"]')) return;
  // Draft/preview visits must never send events into the production property.
  if (window.Shopify?.theme?.role !== 'main') return;
  if (window.__ypGa4ConfirmedInquiryV1) return;
  window.__ypGa4ConfirmedInquiryV1 = true;
  let sent = false;
  const maybeSend = () => {
    if (sent) return;
    const privacy = window.Shopify?.customerPrivacy;
    try {
      if (!privacy || privacy.analyticsProcessingAllowed() !== true) return;
    } catch (_error) { return; }
    window.dataLayer = window.dataLayer || [];
    const gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    gtag('event', 'generate_lead', {
      send_to:'G-J7ZLPKD29W',
      form_name:name,
      lead_type:'submitted_inquiry',
      page_location:window.location.origin + window.location.pathname,
      transport_type:'beacon'
    });
    sent = true;
    document.removeEventListener('visitorConsentCollected', maybeSend);
    document.removeEventListener('shopifyCustomerPrivacyApiLoaded', maybeSend);
  };
  document.addEventListener('visitorConsentCollected', maybeSend);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', maybeSend);
  if (window.Shopify.customerPrivacy) maybeSend();
  else if (typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], error => {
      if (!error) maybeSend();
    });
  }
})();
